import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { vehiclesAPI, tenantUsersAPI } from '../../api';
import VehicleForm from './VehicleForm';
import Layout from '../../components/Layout';
import { Tooltip, TooltipProvider } from '../../components/ui/Tooltip';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card, CardContent } from '../../components/ui/Card';
import SlideOver from '../../components/ui/SlideOver';
import { FormLabel } from '../../components/ui/FormLabel';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../components/ui/Table';
import { Edit, Trash2, Eye } from 'lucide-react';

const TenantVehicles = () => {
  const navigate = useNavigate();

  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('true');
  const [assignedFilter, setAssignedFilter] = useState('');
  const [tenantUsers, setTenantUsers] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  useEffect(() => {
    loadTenantUsers();
  }, []);

  useEffect(() => {
    fetchVehicles();
  }, [pagination.page, search, activeFilter, assignedFilter]);

  const loadTenantUsers = async () => {
    try {
      const response = await tenantUsersAPI.list();
      const users = response?.users || response || [];
      setTenantUsers(Array.isArray(users) ? users : []);
    } catch (err) {
      console.error('Failed to load tenant users:', err);
    }
  };

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: search.trim() || undefined,
        is_active: activeFilter || undefined,
        assigned_user_id: assignedFilter === '__none__' ? '__none__' : assignedFilter || undefined,
      };
      const response = await vehiclesAPI.getAll(params);

      setVehicles(response.vehicles || []);
      setPagination((prev) => ({
        ...prev,
        ...(response.pagination || {}),
      }));
    } catch (err) {
      setError(err.message || 'Failed to load vehicles');
      console.error('Error fetching vehicles:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVehicle = async (vehicleId) => {
    if (!confirm('Are you sure you want to deactivate this vehicle?')) return;

    try {
      await vehiclesAPI.delete(vehicleId);
      fetchVehicles();
    } catch (err) {
      alert(err.message || 'Failed to deactivate vehicle');
      console.error('Delete error:', err);
    }
  };

  const formatMileage = (val) => {
    if (val == null || val === '') return '—';
    return new Intl.NumberFormat('en-GB').format(val);
  };

  return (
    <TooltipProvider>
      <Layout>
        {/* Header */}
        <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="md:flex md:items-center md:justify-between">
              <div className="flex-1 min-w-0">
                <h1 className="text-display text-[var(--ds-text-primary)] font-bold">
                  Vehicles / Fleet
                </h1>
                <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
                  Manage fleet vehicles, service schedules, and mileage
                </p>
              </div>
              <div className="mt-4 flex md:mt-0 md:ml-4 gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/tenant/dashboard')}
                >
                  ← Back to Dashboard
                </Button>
                <Button variant="primary" onClick={() => setShowCreateModal(true)}>
                  + Add Vehicle
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="md:col-span-2">
                  <FormLabel className="text-gray-700 dark:text-gray-300">Search</FormLabel>
                  <Input
                    type="text"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPagination((prev) => ({ ...prev, page: 1 }));
                    }}
                    placeholder="Reg, make, model, or VIN..."
                    className="h-9 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
                <div>
                  <FormLabel className="text-gray-700 dark:text-gray-300">Assigned to</FormLabel>
                  <select
                    value={assignedFilter}
                    onChange={(e) => {
                      setAssignedFilter(e.target.value);
                      setPagination((prev) => ({ ...prev, page: 1 }));
                    }}
                    className="flex h-9 w-full border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All</option>
                    <option value="__none__">Unassigned</option>
                    {tenantUsers
                      .filter((u) => ['tenant_admin', 'tenant_user'].includes(u.role))
                      .map((u) => (
                        <option key={u.id} value={u.id}>
                          {[u.firstName ?? u.first_name, u.lastName ?? u.last_name].filter(Boolean).join(' ').trim() || u.email || 'Unknown'}
                        </option>
                      ))}
                  </select>
                </div>
                <div>
                  <FormLabel className="text-gray-700 dark:text-gray-300">Status</FormLabel>
                  <select
                    value={activeFilter}
                    onChange={(e) => {
                      setActiveFilter(e.target.value);
                      setPagination((prev) => ({ ...prev, page: 1 }));
                    }}
                    className="flex h-9 w-full border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">All</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Error */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {/* Vehicles Table */}
          <Card className="overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--ds-accent-primary)]"></div>
                <p className="mt-2 text-body text-[var(--ds-text-muted)]">Loading vehicles...</p>
              </div>
            ) : vehicles.length === 0 ? (
              <div className="p-8 text-center text-body text-[var(--ds-text-muted)]">
                <p>No vehicles found</p>
                <Button
                  variant="primary"
                  onClick={() => setShowCreateModal(true)}
                  className="mt-4"
                >
                  Add your first vehicle
                </Button>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Vehicle</TableHead>
                      <TableHead>Assigned</TableHead>
                      <TableHead className="text-right">Mileage</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vehicles.map((vehicle) => (
                      <TableRow key={vehicle.id}>
                        <TableCell className="whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-lg bg-[rgba(59,130,246,0.2)] flex items-center justify-center text-[var(--ds-accent-primary)] font-bold text-xs">
                                {vehicle.reg_number?.slice(0, 2) || '—'}
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-[var(--ds-text-primary)]">
                                {vehicle.reg_number || '—'}
                              </div>
                              <div className="text-xs text-[var(--ds-text-muted)]">
                                {[vehicle.make, vehicle.model].filter(Boolean).join(' ') || '—'}
                                {vehicle.year ? ` (${vehicle.year})` : ''}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-sm text-[var(--ds-text-primary)]">
                          {vehicle.assigned_user_name || vehicle.assigned_user_email || '—'}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-right text-sm text-[var(--ds-text-primary)]">
                          {formatMileage(vehicle.current_mileage)}
                        </TableCell>
                        <TableCell>
                          <span
                            className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                              vehicle.is_active
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {vehicle.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-right">
                          <div className="flex items-center justify-end space-x-2">
                            <Tooltip content="View">
                              <Button
                                variant="icon"
                                size="sm"
                                onClick={() => navigate(`/tenant/vehicles/${vehicle.id}`)}
                                className="text-[var(--ds-text-secondary)] hover:text-[var(--ds-text-primary)]"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </Tooltip>
                            <Tooltip content="Edit">
                              <Button
                                variant="icon"
                                size="sm"
                                onClick={() => {
                                  setSelectedVehicle(vehicle);
                                  setShowEditModal(true);
                                }}
                                className="text-[var(--ds-accent-primary)] hover:text-[var(--ds-accent-primary-hover)]"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            </Tooltip>
                            <Tooltip content="Deactivate">
                              <Button
                                variant="icon"
                                size="sm"
                                onClick={() => handleDeleteVehicle(vehicle.id)}
                                disabled={!vehicle.is_active}
                                className="text-[var(--ds-status-danger)] hover:text-[var(--ds-status-danger)]"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Pagination */}
                {pagination.totalPages > 1 && (
                  <Card>
                    <CardContent className="px-4 py-3 sm:px-6 border-t border-[var(--ds-border-divider)]">
                      <div className="flex items-center justify-between">
                        <div className="text-body text-[var(--ds-text-secondary)]">
                          Page <span className="font-medium">{pagination.page}</span> of{' '}
                          <span className="font-medium">{pagination.totalPages}</span>
                          {' '}({pagination.total} vehicles)
                        </div>
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setPagination((prev) => ({ ...prev, page: prev.page - 1 }))
                            }
                            disabled={pagination.page === 1}
                          >
                            Previous
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setPagination((prev) => ({ ...prev, page: prev.page + 1 }))
                            }
                            disabled={pagination.page === pagination.totalPages}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </Card>
        </div>

        {/* Create Slide-Over */}
        <SlideOver
          open={showCreateModal}
          onOpenChange={setShowCreateModal}
          title="Add Vehicle"
          width="w-full max-w-2xl"
        >
          <VehicleForm
            onClose={() => setShowCreateModal(false)}
            onSuccess={() => {
              setShowCreateModal(false);
              fetchVehicles();
            }}
          />
        </SlideOver>

        {/* Edit Slide-Over */}
        {selectedVehicle && (
          <SlideOver
            open={showEditModal}
            onOpenChange={(open) => {
              setShowEditModal(open);
              if (!open) setSelectedVehicle(null);
            }}
            title="Edit Vehicle"
            width="w-full max-w-2xl"
          >
            <VehicleForm
              vehicle={selectedVehicle}
              onClose={() => {
                setShowEditModal(false);
                setSelectedVehicle(null);
              }}
              onSuccess={() => {
                setShowEditModal(false);
                setSelectedVehicle(null);
                fetchVehicles();
              }}
            />
          </SlideOver>
        )}
      </Layout>
    </TooltipProvider>
  );
};

export default TenantVehicles;
